import type { ScenarioStep } from '../core/types';
import type { SystemsContext } from './systems-context';
import type { ScenarioRunner } from './ScenarioRunner';
import { registerCameraSteps } from './steps/camera';
import { registerVehicleSteps } from './steps/vehicle';
import { registerWeatherSteps } from './steps/weather';
import { registerEffectSteps } from './steps/effects';
import { registerHudSteps } from './steps/hud';
import { registerNotificationSteps } from './steps/notification';
import { registerPanelSteps } from './steps/panel';
import { registerScreenSteps } from './steps/screen';
import { registerFlowSteps } from './steps/flow';
import { registerWorldEffectSteps } from './steps/world';

export type StepExecutor = (step: any, systems: SystemsContext, runner: ScenarioRunner, registry: StepRegistry) => Promise<void>;

export class StepRegistry {
  private executors = new Map<string, StepExecutor>();
  private systems: SystemsContext;
  private runner: ScenarioRunner;

  constructor(systems: SystemsContext, runner: ScenarioRunner) {
    this.systems = systems;
    this.runner = runner;

    registerCameraSteps(this);
    registerVehicleSteps(this);
    registerWeatherSteps(this);
    registerEffectSteps(this);
    registerHudSteps(this);
    registerNotificationSteps(this);
    registerPanelSteps(this);
    registerScreenSteps(this);
    registerFlowSteps(this);
    registerWorldEffectSteps(this);
  }

  register(type: string, executor: StepExecutor): void {
    this.executors.set(type, executor);
  }

  async execute(step: ScenarioStep): Promise<void> {
    if (!this.runner.isPlaying()) return;

    const executor = this.executors.get(step.type);
    if (executor) {
      await executor(step, this.systems, this.runner, this);
    } else {
      console.warn(`[StepRegistry] No executor for step type: ${step.type}`);
    }
  }

  async executeInstant(step: ScenarioStep): Promise<void> {
    const type = step.type;

    // For seek: apply side effects instantly without waiting
    // Camera: jump instantly
    if (type === 'camera') {
      const p = (step as any).params;
      this.systems.camera.map.jumpTo({ center: p.center, zoom: p.zoom, pitch: p.pitch ?? 65, bearing: p.bearing ?? 0 });
      return;
    }
    // Weather: apply instantly
    if (type === 'weather') {
      if ((step as any).params.type === 'storm') {
        await this.systems.weather.setStorm(0);
      } else {
        await this.systems.weather.setClear(0);
      }
      return;
    }
    // Spawn vehicle: spawn but don't wait
    if (type === 'spawn-vehicle') {
      const { vehicleId, modelId, origin, destination, label } = (step as any).params;
      const route = this.systems.vehicles.fetchRoute(origin, destination);
      this.systems.routes.addRoute(vehicleId, route);
      this.systems.vehicles.spawn(vehicleId, modelId, route, step.duration, { label });
      this.runner.trackVehicle(vehicleId);
      return;
    }
    // HUD events: emit instantly
    if (type === 'hud-event' || type === 'hud-region' || type === 'hud-team' || type === 'hud-weather') {
      const executor = this.executors.get(type);
      if (executor) await executor(step, this.systems, this.runner, this);
      return;
    }
    // Parallel: fast-execute all sub-steps
    if (type === 'parallel') {
      const subSteps = (step as any).params.steps as ScenarioStep[];
      for (const s of subSteps) {
        await this.executeInstant(s);
      }
      return;
    }
    // World-effect toolkit: start the effect / emit the HUD state instantly
    // (the executor's only blocking part is its trailing pacing wait).
    if (
      type === 'geyser' || type === 'excavation' || type === 'pipe-lay' ||
      type === 'flow-pulse' || type === 'comms-broadcast' || type === 'multi-site-rag' ||
      type === 'color-grade' || type === 'hud-metric' || type === 'clear-effect' ||
      type === 'highlight-buildings' ||
      type === 'convergence' || type === 'match-beam' || type === 'gauge-cluster'
    ) {
      const fast = { ...step, duration: 0 } as ScenarioStep;
      const executor = this.executors.get(type);
      if (executor) await executor(fast, this.systems, this.runner, this);
      return;
    }
    // Skip: wait, notification, panel, screen, effects (don't need to replay)
  }
}
